import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, gql, useLazyQuery } from '@apollo/client';
import { theme } from '../Theme';
import AudioPlayer from './AudioPlayer';
import Loading from './Loading';
import { getCookie } from '../utils/cookies';
import { getTrackInfo, logTrackAccessOnBlockchain } from '../utils/blockchain';

const GET_MUSIC_TRACKS_QUERY = gql`
  query getMusicTracks {
    getMusicTracks {
      id
      trackHolder
      artist
      songName
      recordLabel
      bpm
      genre
      releaseDate
      format
      length
      imageUrl
      musicUrl
      imageIv
      musicIv
    }
  }
`;

const DECRYPT_TRACK_QUERY = gql`
  query DecryptTrack($id: ID!) {
    decryptTrack(id: $id)
  }
`;

const FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  height: 100vh;
  background-color: ${theme.colors.secondary};
`;

const TrackItem = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.accent};
  padding: 10px;
  margin: 10px;
  width: 100%;
  max-width: 600px;
  border-radius: 50%;
  color: ${theme.colors.textSecondary};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  height: 300px;
  width: 300px;
  align-items: center;
  justify-content: center;
`;

const TrackDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-right: 20px;
  justify-content: center;
  align-items: stretch;
  flex-wrap: nowrap;
`;

const TrackTitle = styled.h2`
  font-size: 18px;
  margin: 0;
  color: ${theme.colors.textPrimary};
`;

const TrackArtist = styled.p`
  margin: 5px 0 0;
  color: ${theme.colors.textSecondary};
`;

const NoTracksMessage = styled.p`
  font-size: 18px;
  color: ${theme.colors.textSecondary};
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 600px;
  margin: 20px 0;
  gap: 10px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ControlButton = styled.button`
  padding: 10px 20px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.secondary};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  flex: 1;

  &:hover {
    background-color: ${theme.colors.accent};
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const Feed = () => {
  const { data, loading, error, refetch } = useQuery(GET_MUSIC_TRACKS_QUERY);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isTrackLoading, setIsTrackLoading] = useState(false);
  const [currentTrackUrl, setCurrentTrackUrl] = useState(null);
  const [blockchainInfo, setBlockchainInfo] = useState(null);

  const [decryptTrack] = useLazyQuery(DECRYPT_TRACK_QUERY, {
    onCompleted: (data) => {
      if (data.decryptTrack) {
        const url = `data:audio/mpeg;base64,${data.decryptTrack}`;
        setCurrentTrackUrl(url);
      }
      setIsTrackLoading(false);
    },
    onError: (error) => {
      console.error('Error decrypting track:', error.message);
      setIsTrackLoading(false);
    }
  });

  const tracks = data?.getMusicTracks || [];

  const playTrack = async (index) => {
    if (index < 0 || index >= tracks.length) return;
    const track = tracks[index];
    setIsTrackLoading(true);

    const token = getCookie('token');
    decryptTrack({
      variables: { id: track.id },
      context: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    setCurrentTrackIndex(index);

    try {
      // Log track access on blockchain
      await logTrackAccessOnBlockchain(track.id);
      console.log(`Logged access for track: ${track.songName}`);

      // Get track info from blockchain
      const info = await getTrackInfo(track.id);
      setBlockchainInfo(info);
      console.log('Blockchain info:', info);
    } catch (error) {
      console.error('Error interacting with blockchain:', error);
    }
  };

  const handleNextTrack = () => {
    playTrack(currentTrackIndex + 1);
  };

  const handlePreviousTrack = () => {
    playTrack(currentTrackIndex - 1);
  };

  const handleTrackEnd = () => {
    handleNextTrack();
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (tracks.length > 0) {
      playTrack(0); // Play the first track initially
    }
  }, [tracks]);

  if (loading) return <Loading />;
  if (error) {
    console.error('GraphQL query error:', error);
    return <p>Error: {error.message}</p>;
  }

  const currentTrack = tracks[currentTrackIndex];

  return (
    <FeedContainer>
      <h1>Click To Play</h1>
      {tracks.length === 0 ? (
        <NoTracksMessage>No tracks available.</NoTracksMessage>
      ) : (
        currentTrack &&  isTrackLoading && (
        <TrackItem onClick={() => !isTrackLoading && playTrack(currentTrackIndex)} disabled={isTrackLoading}>
          <TrackDetails>
            <TrackTitle>{currentTrack.songName}</TrackTitle>
            <TrackArtist>{currentTrack.artist}</TrackArtist>
            {isTrackLoading && <p>Loading...</p>}
            {blockchainInfo && (
              <div>
                <p>Plays: {blockchainInfo.plays.toString()}</p>
                <p>Last Played: {new Date(blockchainInfo.lastPlayed * 1000).toLocaleString()}</p>
              </div>
            )}
          </TrackDetails>
        </TrackItem>
        )
      )}
      {currentTrackUrl && !isTrackLoading && (
        <div>
          <AudioPlayer src={currentTrackUrl} isLoading={isTrackLoading} onEnded={handleTrackEnd} />
          <ControlsContainer>
            <ControlButton onClick={handlePreviousTrack}>Previous</ControlButton>
            <ControlButton onClick={handleNextTrack}>Next</ControlButton>
          </ControlsContainer>
        </div>
      )}
    </FeedContainer>
  );
};

export default Feed;
